import { Box, Button, styled, Typography } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertFillIcon } from '~/assets/icons';
import { BASE_COLORS } from '~/utils/constants/common';
import AuthLayout from '../AuthLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminRoute } from '~/utils/constants/route';
import authApi from '~/apis/auth';
import { handleApiError, notify } from '~/utils/helper';

const InputElement = styled('input')(
  ({ theme }) => `
  width: 40px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 0;
  border-radius: 8px;
  text-align: center;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
  box-shadow: 0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'};
  border: 1px solid ${BASE_COLORS.BLUE_BOLD};
  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? blue[600] : blue[200]};
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }
`
);

const blue = {
  100: '#DAECFF',
  200: '#80BFFF',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  700: '#0059B2',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

function OTP({
  length,
  value,
  onChange,
}: {
  length: number;
  value: string;
  onChange: React.Dispatch<React.SetStateAction<string>>;
}) {
  const inputRefs = React.useRef<HTMLInputElement[]>(new Array(length).fill(null));

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case 'Delete':
        event.preventDefault();
        onChange((prevOtp) => {
          const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case 'Backspace':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange((prevOtp) => {
          const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, currentIndex: number) => {
    const currentValue = event.target.value;
    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
        indexToEnter += 1;
      } else {
        break;
      }
    }
    onChange((prev) => {
      const otpArray = prev.split('');
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join('');
    });
    if (currentValue !== '') {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1);
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>, currentIndex: number) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, currentIndex: number) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    if (clipboardData.types.includes('text/plain')) {
      let pastedText = clipboardData.getData('text/plain');
      pastedText = pastedText.substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split('');

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? ' ';
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(''));
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
          <InputElement
            aria-label={`Digit ${index + 1} of OTP`}
            ref={(ele) => {
              inputRefs.current[index] = ele!;
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onChange={(event) => handleChange(event, index)}
            onClick={(event) => handleClick(event, index)}
            onPaste={(event) => handlePaste(event, index)}
            value={value[index] ?? ''}
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

const OtpConfirmation = () => {
  const { t } = useTranslation('admin/auth');
  const [otp, setOtp] = React.useState('');
  const [messageError, setMessageError] = useState<string>();
  const [time, setTime] = useState(180);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone');

  React.useEffect(() => {
    if (time <= 0) return;
    const interval = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setMessageError(t('otpConfirmation.otpExpired'));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [time]);

  const minutes = String(Math.floor(time / 60)).padStart(2, '0');
  const seconds = time % 60;

  const validateOTP = (otp: string) => {
    const otpRegex = /^[0-9]{6}$/;

    let messageError = otp
      ? otpRegex.test(otp)
        ? ''
        : t('otpConfirmation.otpInvalid')
      : t('otpConfirmation.otpRequired');

    if (time <= 0) {
      messageError = t('otpConfirmation.otpExpired');
    }
    return messageError;
  };

  const handleVerifyOTP = async () => {
    if (validateOTP(otp)) {
      setMessageError(validateOTP(otp));
      return;
    }
    try {
      await authApi.verifyOTPForgotPassword({ phone, otp });
      notify('success', t('otpConfirmation.otpVerifySuccess'));
      navigate(`${adminRoute.base}${adminRoute.resetPassword}?phone=${phone}`);
    } catch (error) {
      if (!phone) {
        notify('error', t('validation.phoneInvalid'));
        return;
      }
      handleApiError(error);
    }
  };

  const handleResendOTP = async () => {
    try {
      await authApi.forgertPasswordRequestOTP({ phone });
      notify('success', t('otpConfirmation.otpSendSuccess'));
      setTime(120);
      setMessageError('');
    } catch (error: any) {
      if (!phone) {
        notify('error', t('validation.phoneInvalid'));
        return;
      }
      handleApiError(error);
    }
  };

  return (
    <AuthLayout>
      <Typography className='text-center font-bold text-[#ED1002] lg:text-[40px] xl:text-[44px]'>{`${t(
        'forgotPassword.title'
      ).toUpperCase()}`}</Typography>
      <Box>
        <Typography className='xl1440:text-[20px] whitespace-pre-line text-center'>
          {t('forgotPassword.secondGuide')}
        </Typography>
        <Box className='px-7 mt-8'>
          <Box className='flex justify-center'>
            <OTP value={otp} onChange={setOtp} length={6} />
          </Box>
          {messageError && (
            <Typography color='error' className='text-sm mt-2 flex items-end gap-[6px]'>
              <AlertFillIcon fill={BASE_COLORS.RED} />
              {messageError}
            </Typography>
          )}
          <Box className='mt-5 flex justify-between'>
            <Box>
              <Typography className='lg:text-[14px] xl1440:text-[16px]'>
                {t('forgotPassword.notReceivedOtp')}
              </Typography>
              <Typography
                className='lg:text-[14px] xl1440:text-[16px] font-bold underline cursor-pointer'
                onClick={handleResendOTP}
              >
                {t('forgotPassword.resendOtp')}
              </Typography>
            </Box>
            <Box className='text-xl font-bold text-[#ED1002] underline lg:text-[14px] xl1440:text-[16px] px-6'>
              {minutes}:{seconds.toString().padStart(2, '0')}
            </Box>
          </Box>
          <Box className='flex justify-evenly mt-10'>
            <Button
              className={`shadow-[0px_4px_4px_0px_#00000040] text-[${BASE_COLORS.BLUE_BOLD}] lg:text-[16px] xl1440:text-[18px] px-6`}
              onClick={() => {
                navigate(`${adminRoute.base}${adminRoute.forgotPassword}`);
              }}
            >
              {t('forgotPassword.cancel')}
            </Button>
            <Button
              className={`shadow-[0px_4px_4px_0px_#00000040] bg-[#ED1002] text-white lg:text-[16px] xl1440:text-[18px] px-6`}
              sx={{
                '&:hover': {
                  backgroundColor: '#ED1002',
                },
              }}
              onClick={handleVerifyOTP}
            >
              {t('forgotPassword.continue')}
            </Button>
          </Box>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default OtpConfirmation;
